import React from 'react'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { PartialShoesAction, ShoesState } from 'src/types'
import { generatePerPartsShoesDispatcher, generateShoesDispatcher, getShoesObjects, getShoesParts, getUpperMaterial } from '../../logic'
import {
  ATOMS_LABELS,
  COLORS,
  LACE_VAMP_STRAPS,
  LINES,
  MAGIC_BELT_BASES,
  MAGIC_BELT_MATERIALS,
  MAGIC_BELT_VAMP_STRAPS,
  MESH_COLORS,
  STITCHES,
  TOE_GUARD_COLORS,
  TONGUE_LININGS
} from '../../constants/colors'
import { SelectCardWithImage } from 'src/Components/Ui/SelectCardWithImage'

type Props = {
  state: ShoesState
  selectedIndex: number
  dispatch: React.Dispatch<PartialShoesAction>
}

export const ShoesColorSetter: React.FC<Props> = ({ state, selectedIndex, dispatch }) => {
  const {
    upperMaterial,
    tongue,
    parts,
    mainUnit,
    tongueFront,
    line,
    line2,
    vampStrap,
    vampStrapBelt1,
    vampStrapBelt2,
    vampStrapBelt3,
    stitch,
    tongueLinings,
    middleCutOpening,
    toeGuardColor,
    atomsLabel,
    lineType,
    vampStrapType,
    magicBeltMaterial,
    magicBeltBase,
    openingPattern,
    toeGuard
  } = state
  const partsKey = parts.value

  const handle = {
    [partsKey]: generatePerPartsShoesDispatcher(dispatch)(COLORS[upperMaterial.value])(partsKey),
    parts: generateShoesDispatcher(dispatch)('parts'),
    mainUnitMesh: generateShoesDispatcher(dispatch)('mainUnit'),
    tongueFrontMesh: generateShoesDispatcher(dispatch)('tongueFront'),
    line: generateShoesDispatcher(dispatch)('line'),
    line2: generateShoesDispatcher(dispatch)('line2'),
    vampStrap: generateShoesDispatcher(dispatch)('vampStrap'),
    vampStrapBelt1: generateShoesDispatcher(dispatch)('vampStrapBelt1'),
    vampStrapBelt2: generateShoesDispatcher(dispatch)('vampStrapBelt2'),
    vampStrapBelt3: generateShoesDispatcher(dispatch)('vampStrapBelt3'),
    stitch: generateShoesDispatcher(dispatch)('stitch'),
    tongueLinings: generateShoesDispatcher(dispatch)('tongueLinings'),
    middleCutOpening: generateShoesDispatcher(dispatch)('middleCutOpening'),
    magicBeltMaterial: generateShoesDispatcher(dispatch)('magicBeltMaterial'),
    magicBeltBase: generateShoesDispatcher(dispatch)('magicBeltBase'),
    toeGuardColor: generateShoesDispatcher(dispatch)('toeGuardColor'),
    atomsLabel: generateShoesDispatcher(dispatch)('atomsLabel')
  }
  const colorObjects = getShoesObjects(state)
  const isMagicBelt = ['beltTypeSameDirection', 'beltTypeReverseDirection'].includes(vampStrapType.value)
  const partsLabel = parts.label
  const { selectableParts } = getShoesParts(state)
  const { isMesh, isTongueFrontMesh } = getUpperMaterial(state)
  if (!colorObjects) return <></>
  return (
    <TabPanel {...{ selectedIndex }} index={1}>
      <SelectCard
        summary={'パーツ'}
        selectedLabel={partsLabel}
        objects={selectableParts}
        handleChange={handle.parts}
        isError={parts.value === 'unselected'}
      />
      <SelectCard
        summary={`${partsLabel}カラー`}
        selectedLabel={state[partsKey].label}
        selectedColor={state[partsKey].color}
        objects={COLORS[upperMaterial.value]}
        description={upperMaterial.value === 'unselected' ? '"アッパー素材"を先に選択してください' : ''}
        handleChange={handle[partsKey]}
        isError={state[partsKey].value === 'unselected'}
      />
      <SelectCard
        summary={'本体メッシュカラー'}
        selectedLabel={mainUnit.label}
        selectedColor={mainUnit.color}
        objects={MESH_COLORS}
        isDisplay={isMesh}
        description={tongue.value === 'unselected' ? 'パーツ設定 > "ベロタイプ"を先に選択してください' : ''}
        handleChange={handle.mainUnitMesh}
        disabled={[tongue.value].includes('unselected')}
        isError={[mainUnit.value, tongue.value].includes('unselected')}
      />
      <SelectCard
        summary={`ベロ表メッシュカラー`}
        selectedLabel={tongueFront.label}
        selectedColor={tongueFront.color}
        objects={MESH_COLORS}
        isDisplay={isTongueFrontMesh}
        handleChange={handle.tongueFrontMesh}
        isError={tongueFront.value === 'unselected'}
      />

      <SelectCard
        summary={'ラインカラー'}
        selectedLabel={line.label}
        selectedColor={line.color}
        objects={LINES}
        description={'エナメル素材です'}
        handleChange={handle.line}
        isError={line.value === 'unselected'}
      />
      <SelectCard
        summary={'ラインカラー2'}
        selectedLabel={line2.label}
        selectedColor={line2.color}
        objects={LINES}
        description={'エナメル素材です。'}
        handleChange={handle.line2}
        isDisplay={lineType.value === 'doubleLine'}
        isError={line2.value === 'unselected'}
      />
      <SelectCard
        summary={'マジックベルトの素材'}
        selectedLabel={magicBeltMaterial.label}
        objects={MAGIC_BELT_MATERIALS}
        isDisplay={isMagicBelt}
        disabled={vampStrap.value !== 'unselected'}
        description={vampStrap.value !== 'unselected' ? '再選択するには、ベルトカラーをリセットしてください。' : ''}
        handleChange={handle.magicBeltMaterial}
        isError={magicBeltMaterial.value === 'unselected'}
      />
      <SelectCard
        summary={`紐、ベルトカラー${isMagicBelt ? `（${magicBeltMaterial.label}）` : ''}`}
        selectedLabel={vampStrap.label}
        selectedColor={vampStrap.color}
        objects={isMagicBelt ? MAGIC_BELT_VAMP_STRAPS[magicBeltMaterial.value] : LACE_VAMP_STRAPS}
        isDisplay={magicBeltMaterial.value !== 'unselected' || vampStrapType.value === 'laceType'}
        handleChange={handle.vampStrap}
        isError={vampStrap.value === 'unselected'}
      />
      <SelectCard
        summary={`ベルトカラー1（${magicBeltMaterial.label}`}
        selectedLabel={vampStrapBelt1.label}
        selectedColor={vampStrapBelt1.color}
        objects={MAGIC_BELT_VAMP_STRAPS[magicBeltMaterial.value]}
        isDisplay={isMagicBelt && magicBeltMaterial.value !== 'unselected'}
        defaultExpanded={false}
        isError={vampStrapBelt1.value === 'unselected'}
        handleChange={handle.vampStrapBelt1}
      />
      <SelectCard
        summary={`ベルトカラー2（${magicBeltMaterial.label}`}
        selectedLabel={vampStrapBelt2.label}
        selectedColor={vampStrapBelt2.color}
        objects={MAGIC_BELT_VAMP_STRAPS[magicBeltMaterial.value]}
        isDisplay={isMagicBelt && magicBeltMaterial.value !== 'unselected'}
        defaultExpanded={false}
        isError={vampStrapBelt2.value === 'unselected'}
        handleChange={handle.vampStrapBelt2}
      />
      <SelectCard
        summary={`ベルトカラー3（${magicBeltMaterial.label}`}
        selectedLabel={vampStrapBelt3.label}
        selectedColor={vampStrapBelt3.color}
        objects={MAGIC_BELT_VAMP_STRAPS[magicBeltMaterial.value]}
        isDisplay={isMagicBelt && magicBeltMaterial.value !== 'unselected'}
        defaultExpanded={false}
        isError={vampStrapBelt3.value === 'unselected'}
        handleChange={handle.vampStrapBelt3}
      />
      <SelectCard
        summary={'マジックベルト裏地'}
        selectedLabel={magicBeltBase.label}
        selectedColor={magicBeltBase.color}
        objects={MAGIC_BELT_BASES}
        isDisplay={isMagicBelt}
        handleChange={handle.magicBeltBase}
        isError={magicBeltBase.value === 'unselected'}
      />
      <SelectCard
        summary={'ステッチカラー'}
        selectedLabel={stitch.label}
        selectedColor={stitch.color}
        objects={STITCHES}
        handleChange={handle.stitch}
        caution={'ラインデザイン上のステッチカラーは、ラインカラーと同色になります。'}
        isError={stitch.value === 'unselected'}
      />
      <SelectCard
        summary={'口革(ベロ裏)カラー'} // 口革 === ベロ裏で正しいか確認
        selectedLabel={tongueLinings.label}
        selectedColor={tongueLinings.color}
        objects={TONGUE_LININGS}
        handleChange={handle.tongueLinings}
        isError={tongueLinings.value === 'unselected'}
      />
      <SelectCard
        summary={'ミドルカットカラー'}
        selectedLabel={middleCutOpening.label}
        selectedColor={middleCutOpening.color}
        objects={COLORS['smooth']}
        description={'素材はトリベラスムースです。'}
        isDisplay={['mountainMiddleCut', 'horizontalMiddleCut'].includes(openingPattern.value)}
        handleChange={handle.middleCutOpening}
        isError={middleCutOpening.value === 'unselected'}
      />
      <SelectCard
        summary={'P革カラー'}
        selectedLabel={toeGuardColor.label}
        selectedColor={toeGuardColor.color}
        objects={TOE_GUARD_COLORS}
        isDisplay={!['none', 'unselected'].includes(toeGuard.value)}
        handleChange={handle.toeGuardColor}
        isError={toeGuardColor.value === 'unselected'}
      />
      <SelectCardWithImage
        summary={'ラベル'}
        selectedLabel={atomsLabel.label}
        selectedColor={atomsLabel.color}
        objects={ATOMS_LABELS}
        imageWidth={150}
        handleChange={handle.atomsLabel}
        isError={atomsLabel.value === 'unselected'}
      />
    </TabPanel>
  )
}
